import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import * as Sentry from '@sentry/browser';
import format from 'string-template';
import { useRouteMatch } from 'react-router-dom';

import {
  Checkbox,
  ListItemText,
  MenuItem,
  Select,
} from '@mui/material';

import Product from '../../../../../Model/Product';
import LibraryDoc from '../../../../Model/LibraryDoc';
import useSessionStore from '../../../../../hooks/useSessionStore';
import useStorage from '../../../../../hooks/useStorage';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import { SaveButton } from '../../../../../components/Button/ActionButtons';
import ClientDocumentItem from '../../../../../components/ClientDocumentsPanel/components/ClientDocumentItem';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import { withCustomizationContextReady } from '../../../../context/CustomizationContext';
import useToast from '../../../../hooks/useToast';
import AttachmentsContainer from '../../../../components/AttachmentsContainer';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import { InfoTag } from '../../../../../components/Tags';
import useAttachmentInputHandlers from '../../../../../hooks/useAttachmentInputHandlers';
import { MAX_FILE_UPLOAD_SIZE, MAX_FILE_UPLOAD_SIZE_MB } from '../../../../../utils/files';

import commonTexts from '../texts.json';
import { Title, Description } from '../../styles';
import { countClientsOnProducts, getCoachClients, uploadLibraryDocuments } from './utils';
import {
  AudienceNote,
  ContentWrapper,
  EmptyState,
  FieldLabel,
  SectionActions,
  SectionContainer,
  SectionHint,
  SectionTitle,
  SelectedProducts,
} from './styles';

import screenTexts from './texts.json';

const texts = { ...commonTexts, ...screenTexts };

/**
 * Ongoing document delivery into clients' Libraries, per product.
 *
 * Deliberately not part of Welcome Message: that surface delivers once, at
 * activation, and documents added six months later have nothing to do with a
 * welcome. This tab is revisitable, and a save here reaches clients who are
 * already live.
 *
 * Applies to every product, not only Challenges. What is published here is addressed
 * to a product, but the Library it lands in belongs to the client rather than to
 * chat, so nothing underneath is particular to a Challenge; how a client *reaches*
 * their Library still differs (chat clients open it from the chat interface,
 * chat-free clients get the bottom-nav tab), but that is a client-side concern and
 * not something this page decides.
 *
 * Files only, with no message field — a document published here can land for clients
 * who have no chat, so there is no surface a message could reliably appear in.
 *
 * The page writes only the coach's own record of what was published. Fanning each
 * document into every client's Library, and removing it again, is the server's job.
 */
const LibraryDocumentsConfig = () => {
  const { params: { userId: coachId } } = useRouteMatch();
  const { userId: signedInUserId, authUser } = useSessionStore();
  // sessionStore exposes no display name; the Firebase user carries one.
  const signedInUserName = authUser?.displayName || '';

  const { uploadAttachments, deleteAttachments } = useStorage();
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();

  const { onFilesSelected, removeFileByIndex } = useAttachmentInputHandlers({
    showToast,
    fileSizeErrorText: texts.fileSizeError,
    maxFileSizeBytes: MAX_FILE_UPLOAD_SIZE,
    maxFileSizeMb: MAX_FILE_UPLOAD_SIZE_MB,
  });

  const [products, setProducts] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [publishedDocs, setPublishedDocs] = useState([]);
  const [coachClients, setCoachClients] = useState(null);
  const [hasRosterError, setHasRosterError] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false);

  const productNameById = useMemo(
    () => Object.fromEntries(products.map(({ id, name }) => [id, name])),
    [products],
  );

  /*
    Loaded independently of the products below. A failure here — the collection
    group needs its own index, and its rules are not deployed yet — must not take
    the product list down with it, or the page reads as "you have no products" when
    the coach plainly has some.
  */
  const loadDocs = useCallback(async () => {
    try {
      const docs = await LibraryDoc.getAllByCoachId(coachId);
      if (isComponentMountedRef.current) setPublishedDocs(docs);
      return docs;
    } catch (error) {
      Sentry.captureException(error, { extra: { coachId } });
      showToast(texts.loadDocumentsError, { error: true });
      return null;
    }
  }, [coachId, showToast, isComponentMountedRef]);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const productCollection = await Product.getProductByCoachId(coachId);

        // Every product the coach sells. Archived and unnamed ones are not sellable,
        // so no new client can arrive on them.
        const sellable = productCollection.docs
          .filter((product) => !!product.name && !product.isArchived)
          .sort((first, second) => first.name.localeCompare(second.name));

        if (isComponentMountedRef.current) setProducts(sellable);
      } catch (error) {
        Sentry.captureException(error, { extra: { coachId } });
        showToast(texts.loadError, { error: true });
      } finally {
        if (isComponentMountedRef.current) setIsReady(true);
      }
    };

    loadProducts();
  }, [coachId, showToast, isComponentMountedRef]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  /*
    One roster read for the page. Every count below is a filter over it: the
    selection's total for the publish dialog, and one per row for delete, which
    each name a different set of products but the same clients.
  */
  useEffect(() => {
    let isStale = false;

    getCoachClients(coachId).then((clients) => {
      if (isStale || !isComponentMountedRef.current) return;
      if (clients) setCoachClients(clients); else setHasRosterError(true);
    });

    return () => { isStale = true; };
  }, [coachId, isComponentMountedRef]);

  const clientCount = useMemo(
    () => countClientsOnProducts(coachClients ?? [], selectedProductIds),
    [coachClients, selectedProductIds],
  );

  /*
    One publish writes a record per product, so the same file appears once per
    library. Grouped back together here by assetPath, which every record of one
    publish shares — the coach added one document and should see one row — and
    presented as a ClientDocument-shaped
    object so the Chat Documents row renders it: rename and delete apply to every
    record in the group, which is what stops one file living under two names.
  */
  const documentGroups = useMemo(() => {
    const groups = new Map();

    publishedDocs.forEach((doc) => {
      // Falls back to the document id so a record missing its path stands alone,
      // rather than every such record merging into one row whose rename and delete
      // would then hit all of them.
      const key = doc.assetPath || doc.id;
      const existing = groups.get(key);

      if (existing) {
        existing.refs.push({ productId: doc.productId, id: doc.id });
        existing.productIds.push(doc.productId);
        return;
      }

      groups.set(key, {
        key,
        refs: [{ productId: doc.productId, id: doc.id }],
        productIds: [doc.productId],
        fileName: doc.fileName,
        mimeType: doc.mimeType,
        type: doc.type,
        fileSize: doc.fileSize,
        assetPath: doc.assetPath,
        uploadedAt: doc.uploadedAt,
        uploadedByName: doc.uploadedByName,
        source: doc.source,
        isSaved: false,
      });
    });

    return [...groups.values()];
  }, [publishedDocs]);

  /*
    The list answers "what is in these libraries", so with nothing selected there is
    no question to answer — the section is not rendered at all rather than falling
    back to everything the coach has ever published.
  */
  const visibleGroups = useMemo(() => documentGroups.filter((group) => (
    group.productIds.some((productId) => selectedProductIds.includes(productId))
  )), [documentGroups, selectedProductIds]);

  const handlePublish = useCallback(async () => {
    setIsSaveConfirmOpen(false);
    setIsUploading(true);

    try {
      const { uploaded, hasError } = await uploadLibraryDocuments({
        attachments: pendingFiles,
        coachId,
        uploadAttachments,
        deleteAttachments,
      });

      if (hasError) {
        /*
          The staged files cannot be retried: the upload path revokes each object
          url whether it succeeded or failed, so a second attempt dies in fetch
          before reaching Storage and reports that instead of the real cause.
          Cleared rather than left in place, so the coach is told to re-pick rather
          than clicking into a loop.
        */
        if (isComponentMountedRef.current) setPendingFiles([]);
        showToast(texts.uploadError, { error: true });
        return;
      }

      await Promise.all(uploaded.map((file) => LibraryDoc.publish(coachId, selectedProductIds, {
        ...file,
        uploadedBy: signedInUserId,
        uploadedByName: signedInUserName,
      })));

      if (isComponentMountedRef.current) setPendingFiles([]);
      await loadDocs();
      showToast(texts.saved);
    } catch (error) {
      Sentry.captureException(error, { extra: { coachId, selectedProductIds } });
      showToast(texts.saveError, { error: true });
    } finally {
      if (isComponentMountedRef.current) setIsUploading(false);
    }
  }, [
    pendingFiles,
    coachId,
    selectedProductIds,
    signedInUserId,
    signedInUserName,
    uploadAttachments,
    deleteAttachments,
    loadDocs,
    showToast,
    isComponentMountedRef,
  ]);

  const requestPublish = useCallback(() => {
    if (!selectedProductIds.length) {
      showToast(texts.noProductSelected, { error: true });
      return;
    }
    if (!pendingFiles.length) {
      showToast(texts.nothingToSave, { error: true });
      return;
    }

    setIsSaveConfirmOpen(true);
  }, [selectedProductIds, pendingFiles, showToast]);

  const audienceNote = useMemo(() => {
    if (!selectedProductIds.length) return texts.audienceNoteNoSelection;
    // An unreadable roster is not an empty one, and the note should not imply it is.
    if (hasRosterError) return texts.audienceNoteCountUnavailable;
    if (!clientCount) return texts.audienceNoteEmpty;
    return format(texts.audienceNote, { count: clientCount, productCount: selectedProductIds.length });
  }, [selectedProductIds, clientCount, hasRosterError]);

  /*
    The row renders a ClientDocument, so each group is handed one: its reads come
    from the group, and rename/remove fan out across every record the group holds.
    Reloading afterwards is what refreshes the list, since these are plain objects
    rather than observable documents.
  */
  /*
    One count per row, because the products a document is in are its own and the
    whole selection's count would be the wrong number for most rows. A filter over
    the roster already loaded, not a read each.
  */
  const deleteClientCounts = useMemo(() => Object.fromEntries(visibleGroups.map((group) => {
    const affected = group.productIds.filter((productId) => selectedProductIds.includes(productId));
    return [group.key, countClientsOnProducts(coachClients ?? [], affected)];
  })), [visibleGroups, selectedProductIds, coachClients]);

  const documentRows = useMemo(() => visibleGroups.map((group) => {
    const document = {
      ...group,
      /*
        Applied to every product the file was published to, not just the selected
        ones. A name is a property of the file rather than of one library, and
        renaming a subset leaves the group with two names — the row then shows
        whichever record the query happened to return first, which reads as the
        rename having silently failed.
      */
      rename: async (fileName) => {
        await LibraryDoc.renameMany(group.refs, fileName);
        await loadDocs();
      },
      /*
        Scoped to the products the coach currently has selected. The row is shown
        because one of them holds the file; withdrawing it from products they
        cannot see is not what the confirmation in front of them describes.
      */
      remove: async () => {
        const refs = group.refs.filter(({ productId }) => selectedProductIds.includes(productId));
        await LibraryDoc.removeMany(refs);

        const remaining = await loadDocs();

        /*
          The record was the only place the storage path was written down, so once no
          record points at the file it is unreachable — a guaranteed orphan rather
          than a shared blob. Answered from the reloaded set rather than a query: a
          collection group filtered on both coachId and assetPath needs each field
          indexed at group scope, and only coachId is.

          Cleanup failing must not read as the delete failing. The records are
          already gone by this point, and saying otherwise sends the coach back to a
          row that is not there.
        */
        if (remaining && !remaining.some((doc) => doc.assetPath === group.assetPath)) {
          try {
            await deleteAttachments([{ storagePath: group.assetPath }]);
          } catch (error) {
            Sentry.captureException(error, { extra: { coachId, assetPath: group.assetPath } });
          }
        }
      },
    };

    /*
      Named on the row itself, not under it: with several libraries selected the
      list interleaves them, and a row that does not say where it belongs leaves
      the coach counting positions.
    */
    const names = group.productIds.map((id) => productNameById[id]).filter(Boolean);

    /*
      Counted against the products this document is actually in, not the whole
      selection. Selecting three and deleting a document held by one would
      otherwise name one product and then quote the clients of all three, which
      the sentence's "on them" makes plainly wrong.
    */
    const affectedProductIds = group.productIds.filter((productId) => selectedProductIds.includes(productId));

    return (
      <ClientDocumentItem
        key={group.key}
        document={document}
        tags={names}
        deleteTexts={{
          title: texts.confirmDeleteTitle,
          content: hasRosterError
            ? format(texts.confirmDeleteContentCountUnavailable, {
              fileName: group.fileName,
              productCount: affectedProductIds.length,
            })
            : format(texts.confirmDeleteContent, {
              fileName: group.fileName,
              productCount: affectedProductIds.length,
              clientCount: deleteClientCounts[group.key] ?? clientCount,
            }),
          confirm: texts.confirmDeleteButton,
          cancel: texts.cancelButton,
        }}
      />
    );
  }), [visibleGroups, productNameById, selectedProductIds, clientCount, deleteClientCounts,
    hasRosterError, coachId, deleteAttachments, loadDocs]);

  return (
    <ContentWrapper>
      <Title>{texts.title}</Title>
      <Description>{texts.description}</Description>

      {isReady && !products.length && <EmptyState>{texts.noProducts}</EmptyState>}

      {!!products.length && (
        <>
          <SectionContainer>
            <FieldLabel>{texts.productsLabel}</FieldLabel>
            <SectionHint>{texts.productsHint}</SectionHint>
            <Select
              multiple
              displayEmpty
              size="small"
              value={selectedProductIds}
              onChange={(event) => setSelectedProductIds(event.target.value)}
              renderValue={(selected) => (selected.length
                ? format(texts.productsSelected, { count: selected.length })
                : texts.productsPlaceholder)}
            >
              {products.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  <Checkbox checked={selectedProductIds.includes(item.id)} />
                  <ListItemText primary={item.name} />
                </MenuItem>
              ))}
            </Select>
            {!!selectedProductIds.length && (
              <SelectedProducts>
                {selectedProductIds.map((id) => <InfoTag key={id}>{productNameById[id]}</InfoTag>)}
              </SelectedProducts>
            )}
          </SectionContainer>

          <SectionContainer>
            <SectionTitle>{texts.uploadHeading}</SectionTitle>
            <SectionHint>{texts.uploadHint}</SectionHint>
            <AudienceNote>{audienceNote}</AudienceNote>
            <AttachmentsContainer
              fileAttachments={pendingFiles}
              onFileRemoved={(index) => removeFileByIndex(index, setPendingFiles)}
              onFilesSelected={(event) => onFilesSelected(event, setPendingFiles)}
              isUploading={isUploading}
            />
            <SectionActions>
              <SaveButton
                onClick={requestPublish}
                disabled={isUploading || (!coachClients && !hasRosterError)}
                $fitToContent
              >
                {texts.saveButton}
              </SaveButton>
            </SectionActions>
          </SectionContainer>

          {!!selectedProductIds.length && (
            <SectionContainer>
              <SectionTitle>{texts.publishedHeading}</SectionTitle>
              {!visibleGroups.length && <EmptyState>{texts.noDocuments}</EmptyState>}
              {documentRows}
            </SectionContainer>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={isSaveConfirmOpen}
        onConfirm={handlePublish}
        onCancel={() => setIsSaveConfirmOpen(false)}
        dialogTexts={{
          title: texts.confirmSaveTitle,
          content: hasRosterError
            ? format(texts.confirmSaveContentCountUnavailable, {
              fileCount: pendingFiles.length,
              productCount: selectedProductIds.length,
            })
            : format(texts.confirmSaveContent, {
              fileCount: pendingFiles.length,
              productCount: selectedProductIds.length,
              clientCount,
            }),
          confirm: texts.confirmSaveButton,
          cancel: texts.cancelButton,
        }}
      />

      <LoadingOverlay isLoading={!isReady} />
    </ContentWrapper>
  );
};

export default compose(
  withCustomizationContextReady,
  observer,
)(LibraryDocumentsConfig);
